import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, CardHeader, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Pagination, Alert, Button, TableSortLabel, Divider, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { detectionApi, embeddingsApi, patternsApi } from '../services/api';
import { Detection, PaginatedResponse } from '../types/api';

interface SortedAudioEvent {
  label: string;
  probability: string;
  rawProbability: number;
}

// Словарь для автоматического перевода системных звуков на русский язык
const translateLabel = (label: string): string => {
  const map: Record<string, string> = {
    'Speech': 'Речь',
    'Speech synthesizer': 'Синтезатор речи',
    'Music': 'Музыка',
    'Beep, bleep': 'Сигнал (бип)',
    'Busy signal': 'Занято',
    'Dial tone': 'Гудок в трубке',
    'Radio': 'Радио',
    'Narration, monologue': 'Монолог',
    'Female speech, woman speaking': 'Женский голос',
    'Male speech, man speaking': 'Мужской голос',
    'Singing': 'Пение',
    'Laughter': 'Смех',
    'Crying, sobbing': 'Плач'
  };
  return map[label] ?? label;
};

// Функция группировки, перевода, фильтрации (>15%) и сортировки событий
const getSortedAudioEvents = (audioEvents?: Record<string, number>): SortedAudioEvent[] => {
  if (!audioEvents) return [];

  const keys = Object.keys(audioEvents);
  const result: SortedAudioEvent[] = [];

  for (let i = 0; i < keys.length; i++) {
    const label = keys[i];
    const val = audioEvents[label];

    // ФИЛЬТРАЦИЯ: оставляем только события с вероятностью 50% и выше
    if (val >= 0.15) {
      result.push({
        label: translateLabel(label),
        probability: `${(val * 100).toFixed(1)}%`,
        rawProbability: val
      });
    }
  }

  // Сортировка по убыванию
  return result.sort((a, b) => b.rawProbability - a.rawProbability);
};

const DetectionsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [items, setItems] = useState<Detection[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioUrlByFile, setAudioUrlByFile] = useState<Record<string, string>>({});
  const [audioUrlByDetectionId, setAudioUrlByDetectionId] = useState<Record<string, string>>({});
  const [embeddingAudioLoadingDetId, setEmbeddingAudioLoadingDetId] = useState<string | null>(null);
  const [dateSort, setDateSort] = useState<'asc' | 'desc'>('desc');
  const [patternTextById, setPatternTextById] = useState<Record<string, string>>({});

  // Состояния для поиска по SessionId
  const [searchQuery, setSearchQuery] = useState(''); // Для ввода в TextField
  const [appliedSessionId, setAppliedSessionId] = useState(''); // Для реального применения в запросе к API

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Передаем примененный sessionId в запрос (если он не пустой)
      const resp = await detectionApi.getDetections(page, pageSize, 'date', dateSort, appliedSessionId || undefined);
      // API shape: { detections, totalCount }
      const detections = (resp as unknown as any).detections || [];
      setItems(detections);
      setTotalCount((resp as unknown as any).totalCount || 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить детекции');
    } finally {
      setLoading(false);
    }
  };

  const sortedItems = React.useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      const da = new Date(a.createdAt).getTime();
      const db = new Date(b.createdAt).getTime();
      return dateSort === 'asc' ? da - db : db - da;
    });
    return arr;
  }, [items, dateSort]);

  const getMethodLabel = (method?: string): string => {
    if (!method || !method.trim()) return 'Неизвестно';
    const map: Record<string, string> = {
      'Word': 'Текстовый шаблон',
      'Voice': 'Голосовой шаблон',
      'Sound': 'Звуковой шаблон',
      'Synthetic': 'Синтетический голос',
      'Acoustic': 'Акустический шаблон',
      'Skip': 'Шаблон из белого списка',
    };
    return map[method] ?? method;
  };

  // Метод применения поиска
  const handleSearch = () => {
    setPage(1); // Сбрасываем на первую страницу при новом поиске
    setAppliedSessionId(searchQuery.trim());
  };

  // Метод очистки поиска
  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(1);
    setAppliedSessionId('');
  };

  useEffect(() => {
    loadData();
  }, [page, dateSort, appliedSessionId]); // Вызов происходит при смене страницы, сортировки или применении поиска

  // Auto-load pattern phrases for rows with patternPhraseId
  useEffect(() => {
    const loadMissingPatterns = async () => {
      const ids = Array.from(new Set(items.map(i => i.patternPhraseId).filter(Boolean))) as string[];
      const missing = ids.filter(id => !patternTextById[id]);
      if (missing.length === 0) return;
      try {
        await Promise.all(missing.map(async (id) => {
          try {
            const p = await patternsApi.getPatternById(id);
            setPatternTextById(prev => ({ ...prev, [id]: p.phrase }));
          } catch {}
        }));
      } catch {}
    };
    loadMissingPatterns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const handleLoadAudio = async (fileName?: string) => {
    if (!fileName) return;
    try {
      const url = await embeddingsApi.getFileUrlByFileName(fileName, 'detections');
      setAudioUrlByFile(prev => ({ ...prev, [fileName]: url }));
    } catch (e) {
      setError('Не удалось получить аудио');
    }
  };

  const handleLoadEmbeddingAudio = async (embeddingId?: string, detectionId?: string) => {
    if (!embeddingId || !detectionId) return;
    try {
      setEmbeddingAudioLoadingDetId(detectionId);
      const emb = await embeddingsApi.getEmbeddingById(embeddingId);
      const fileName = (emb as any)?.fileName as string | undefined;
      if (!fileName) throw new Error('Нет файла у эмбеддинга');
      const url = await embeddingsApi.getFileUrlByFileName(fileName, 'embeddings');
      setAudioUrlByDetectionId(prev => ({ ...prev, [detectionId]: url }));
    } catch (e) {
      setError('Не удалось получить аудио для шаблона');
    } finally {
      setEmbeddingAudioLoadingDetId(null);
    }
  };

  const handleLoadPattern = async (patternId?: string) => {
    if (!patternId) return;
    try {
      const p = await patternsApi.getPatternById(patternId);
      setPatternTextById(prev => ({ ...prev, [patternId]: p.phrase }));
    } catch (e) {
      setError('Не удалось получить шаблон');
    }
  };

  return (
    <Box>
      {/* Верхняя панель со списком поиска */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Детекции</Typography>
        
        {/* Поле поиска по SessionId */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            label="Поиск по Сессии"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 250, backgroundColor: 'background.paper', borderRadius: 1 }}
          />
          <Button variant="contained" color="primary" onClick={handleSearch} size="medium">
            Искать
          </Button>
          {appliedSessionId && (
            <Button variant="text" color="secondary" onClick={handleClearSearch} size="medium">
              Сбросить
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ width: '100%', overflow: 'hidden' }}>
        <CardHeader title="Последние детекции" subheader={`Всего: ${totalCount}`} />
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5 }}>
              {sortedItems.map((d) => (
                <Paper key={d.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>{new Date(d.createdAt).toLocaleString()}</Typography>
                    {d.isDetected ? (
                      <Chip label="Обнаружено" color="success" size="small" />
                    ) : (
                      <Chip label="Не обнаружено" color="error" size="small" />
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Сессия: <b>{d.sessionId}</b>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Метод: <b>{getMethodLabel(d.detectionMethod?.value)}</b>
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {d.patternPhraseId ? (
                      <Typography variant="body2" noWrap title={patternTextById[d.patternPhraseId] || 'Загрузка...'}>
                        {patternTextById[d.patternPhraseId] || 'Загрузка...'}
                      </Typography>
                    ) : d.embeddingId ? (
                      audioUrlByDetectionId[d.id] ? (
                        <audio controls style={{ width: '100%' }} src={audioUrlByDetectionId[d.id]} />
                      ) : (
                        <Button size="small" variant="outlined" disabled={embeddingAudioLoadingDetId === d.id} onClick={() => handleLoadEmbeddingAudio(d.embeddingId, d.id)}>
                          {embeddingAudioLoadingDetId === d.id ? 'Загрузка...' : 'Аудио шаблона'}
                        </Button>
                      )
                    ) : (d.audioEvents && Object.keys(d.audioEvents).length > 0) ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {getSortedAudioEvents(d.audioEvents).map((event, idx) => (
                          <Chip 
                            key={idx} 
                            label={`${event.label} (${event.probability})`} 
                            size="small" 
                            color={idx === 0 ? "primary" : "default"}
                            variant={idx === 0 ? "filled" : "outlined"}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Chip label="Неизвестно" size="small" />
                    )}

                    {d.fileName && (
                      audioUrlByFile[d.fileName] ? (
                        <audio controls style={{ width: '100%' }} src={audioUrlByFile[d.fileName]} />
                      ) : (
                        <Button size="small" variant="outlined" onClick={() => handleLoadAudio(d.fileName)}>Аудио файла</Button>
                      )
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: '100%', tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={dateSort} sx={{ width: '15%' }}>
                      <TableSortLabel
                        active
                        direction={dateSort}
                        onClick={() => setDateSort(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                      >
                        Дата
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '15%' }}>Сессия</TableCell>
                    <TableCell sx={{ width: '15%' }}>Метод</TableCell>
                    <TableCell sx={{ width: '13%' }}>Результат</TableCell>
                    <TableCell sx={{ width: '21%' }}>Шаблон/Аудио</TableCell>
                    <TableCell sx={{ width: '21%' }}>Файл</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedItems.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell sx={{ width: '15%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {new Date(d.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ width: '15%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.sessionId}>
                        {d.sessionId}
                      </TableCell>
                      <TableCell sx={{ width: '15%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {getMethodLabel(d.detectionMethod?.value)}
                      </TableCell>
                      <TableCell sx={{ width: '13%' }}>
                        {d.isDetected ? (
                          <Chip label="Обнаружено" color="success" size="small" />
                        ) : (
                          <Chip label="Не обнаружено" color="error" size="small" />
                        )}
                      </TableCell>
                      <TableCell sx={{ width: '21%', minWidth: 140, overflow: 'hidden' }}>
                        {d.patternPhraseId ? (
                          <Typography variant="body2" noWrap title={patternTextById[d.patternPhraseId] || 'Загрузка...'}>
                            {patternTextById[d.patternPhraseId] || 'Загрузка...'}
                          </Typography>
                        ) : d.embeddingId ? (
                          audioUrlByDetectionId[d.id] ? (
                            <audio controls style={{ width: '100%', maxWidth: '100%' }} src={audioUrlByDetectionId[d.id]} />
                          ) : (
                            <Button size="small" variant="outlined" disabled={embeddingAudioLoadingDetId === d.id} onClick={() => handleLoadEmbeddingAudio(d.embeddingId, d.id)}>
                              {embeddingAudioLoadingDetId === d.id ? 'Загрузка...' : 'Аудио'}
                            </Button>
                          )
                        ) : (d.audioEvents && Object.keys(d.audioEvents).length > 0) ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {getSortedAudioEvents(d.audioEvents).map((event, idx) => (
                              <Chip 
                                key={idx} 
                                label={`${event.label} (${event.probability})`} 
                                size="small" 
                                color={idx === 0 ? "primary" : "default"}
                                variant={idx === 0 ? "filled" : "outlined"}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Chip label="Неизвестно" size="small" />
                        )}
                      </TableCell>
                      <TableCell sx={{ width: '21%', minWidth: 160 }}>
                        {d.fileName ? (
                          audioUrlByFile[d.fileName] ? (
                            <audio controls style={{ width: '100%', maxWidth: '100%' }} src={audioUrlByFile[d.fileName]} />
                          ) : (
                            <Button size="small" variant="outlined" onClick={() => handleLoadAudio(d.fileName)}>Аудио</Button>
                          )
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {totalCount > pageSize && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: { xs: 1.5, sm: 2 } }}>
              <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DetectionsPage;